import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, Phone, Mail, Sprout, Package, Leaf, Users, Scale, GraduationCap, Star, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { heroSlides, aboutUs, services, productCategories, members, societyInfo } from '../mock';

const iconMap = {
  Sprout, Package, Leaf, Users, Scale, GraduationCap
};

const Home = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);

  return (
    <div className="min-h-screen">
      {/* Hero Slider Section */}
      <section id="home" className="relative h-[600px] overflow-hidden">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40 z-10" />
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 z-20 flex items-center">
              <div className="container mx-auto px-4">
                <div className="max-w-3xl text-white">
                  <Badge className="mb-4 bg-emerald-600 hover:bg-emerald-700 text-white border-none">
                    Government Approved
                  </Badge>
                  <h1 className="text-5xl md:text-6xl font-bold mb-4 leading-tight">
                    {slide.title}
                  </h1>
                  <p className="text-xl md:text-2xl mb-8 text-gray-200">
                    {slide.subtitle}
                  </p>
                  <div className="flex gap-4">
                    <Button
                      onClick={() => document.getElementById('products').scrollIntoView({ behavior: 'smooth' })}
                      size="lg"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      Explore Products
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                    <Button
                      onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
                      size="lg"
                      variant="outline"
                      className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white/20"
                    >
                      Learn More
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {/* Slider Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all"
        >
          <ChevronLeft size={24} />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all"
        >
          <ChevronRight size={24} />
        </button>

        {/* Slider Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all ${
                index === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Text Content - Left */}
            <div>
              <Badge className="mb-4 bg-emerald-100 text-emerald-800 hover:bg-emerald-200">
                About Our Society
              </Badge>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">
                {aboutUs.title}
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {aboutUs.description}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {aboutUs.highlights.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2" />
                    <span className="text-gray-700">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Image - Right */}
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-full h-full bg-emerald-100 rounded-lg" />
              <img
                src={aboutUs.image}
                alt="About Us"
                className="relative rounded-lg shadow-xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-800 hover:bg-emerald-200">
              Our Services
            </Badge>
            <h2 className="text-4xl font-bold mb-4 text-gray-900">What We Offer</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive services designed to support farmers and deliver quality products to consumers
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-2 hover:border-emerald-600 hover:shadow-lg transition-all group">
                  <CardHeader>
                    <div className="w-14 h-14 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-colors">
                      <IconComponent className="text-emerald-600 group-hover:text-white transition-colors" size={28} />
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-emerald-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-600 text-white hover:bg-emerald-700">
              Our Products
            </Badge>
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Product Categories</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our range of authentic products from farm to your home
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {productCategories.map((category) => (
              <Card
                key={category.id}
                className="overflow-hidden hover:shadow-2xl transition-all cursor-pointer group border-2 hover:border-emerald-600"
                onClick={() => navigate(category.route)}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-4 left-4 text-white">
                    <p className="text-sm font-medium mb-1">{category.subtitle}</p>
                    <h3 className="text-2xl font-bold">{category.title}</h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-600 mb-4">{category.description}</p>
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white group">
                    View Products
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Members Section */}
      <section id="members" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-800 hover:bg-emerald-200">
              Leadership Team
            </Badge>
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Our Members</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Meet the dedicated leaders driving our society forward
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {members.map((member) => (
              <Card key={member.id} className="text-center hover:shadow-xl transition-all border-2 hover:border-emerald-600 group">
                <CardContent className="pt-8">
                  <div className="relative inline-block mb-4">
                    <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-emerald-600 mx-auto group-hover:border-emerald-700 transition-colors">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-1 text-gray-900">{member.name}</h3>
                  <p className="text-emerald-600 font-semibold mb-3">{member.title}</p>
                  <p className="text-gray-600 text-sm">{member.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section with Map */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-800 hover:bg-emerald-200">
              Get In Touch
            </Badge>
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Contact Us</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're here to serve you. Reach out for any queries or support
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <Card className="h-full border-2">
                <CardHeader>
                  <CardTitle className="text-2xl">Contact Information</CardTitle>
                  <CardDescription>Feel free to reach out to us</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-emerald-600" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Address</h4>
                      <p className="text-gray-600">{societyInfo.address}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="text-emerald-600" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Phone</h4>
                      <a href={`tel:${societyInfo.phone}`} className="text-emerald-600 hover:text-emerald-700">
                        {societyInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="text-emerald-600" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Email</h4>
                      <a href={`mailto:${societyInfo.email}`} className="text-emerald-600 hover:text-emerald-700">
                        {societyInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="pt-6 border-t">
                    <h4 className="font-semibold mb-3">Office Hours</h4>
                    <div className="space-y-2 text-gray-600">
                      <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                      <p>Saturday: 9:00 AM - 2:00 PM</p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Google Map */}
            <div>
              <Card className="h-full border-2 overflow-hidden">
                <div className="h-full min-h-[500px]">
                  <iframe
                    title="Society Location"
                    src={`<iframe src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d880.4760604165077!2d75.71219398116533!3d28.027402449995787!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sin!4v1776461421744!5m2!1sen!2sin" width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full h-full"
                  />
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">{societyInfo.name}</h3>
              <p className="text-gray-400 mb-4">
                {societyInfo.tagline}
              </p>
              <Badge className="bg-emerald-600 text-white hover:bg-emerald-700 border-none">
                Government Approved
              </Badge>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><button onClick={() => document.getElementById('home').scrollIntoView({ behavior: 'smooth' })} className="text-gray-400 hover:text-white transition-colors">Home</button></li>
                <li><button onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })} className="text-gray-400 hover:text-white transition-colors">About Us</button></li>
                <li><button onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })} className="text-gray-400 hover:text-white transition-colors">Services</button></li>
                <li><button onClick={() => document.getElementById('products').scrollIntoView({ behavior: 'smooth' })} className="text-gray-400 hover:text-white transition-colors">Products</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-start gap-2">
                  <MapPin size={18} className="mt-1 flex-shrink-0" />
                  <span>{societyInfo.address}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone size={18} />
                  <a href={`tel:${societyInfo.phone}`} className="hover:text-white transition-colors">{societyInfo.phone}</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail size={18} />
                  <a href={`mailto:${societyInfo.email}`} className="hover:text-white transition-colors">{societyInfo.email}</a>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} {societyInfo.name}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;